import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Calendar, User, CheckCircle, Loader2, Star, Users, GraduationCap } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Mentors = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMentors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchMentors = async () => {
    try {
      const params = {};
      if (filter === 'verified') params.verified_only = true;
      const response = await axios.get(`${API}/mentors`, { params });
      setMentors(response.data);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
    } finally {
      setLoading(false);
    }
  };

  const sessionTypes = {
    mentorship: { label: 'Mentorship', icon: GraduationCap },
    song_review: { label: 'Song Review', icon: Star },
    prayer_session: { label: 'Prayer', icon: Users },
    virtual_concert: { label: 'Concert', icon: Calendar },
  };

  return (
    <Layout>
      <div className="py-6 md:py-8 px-4 md:px-8 pb-40">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FFE500] font-bold mb-2">Connect</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white">Mentorship Hub</h1>
            <p className="text-[#A0A0A0] mt-2 max-w-xl">
              Book sessions with verified gospel mentors. Get guidance on songwriting, production, ministry, and more.
            </p>
          </div>

          {/* Pill Filters */}
          <div className="flex gap-2 mb-8" data-testid="mentor-filters">
            <button
              onClick={() => setFilter('all')}
              className={`pill-btn ${filter === 'all' ? 'pill-btn-active' : 'pill-btn-inactive'}`}
              data-testid="filter-all"
            >
              All Mentors
            </button>
            <button
              onClick={() => setFilter('verified')}
              className={`pill-btn ${filter === 'verified' ? 'pill-btn-active' : 'pill-btn-inactive'}`}
              data-testid="filter-verified"
            >
              Verified Only
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[#FFE500]" />
            </div>
          ) : mentors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {mentors.map((mentor) => (
                <div key={mentor.id} className="bg-[#16181D] rounded-2xl p-6 hover:bg-[#1E2028] transition-all hover:-translate-y-1">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="h-16 w-16 rounded-full bg-[#FFE500] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {mentor.profile_image ? (
                        <img src={mentor.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-7 w-7 text-black" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-white flex items-center gap-1.5">
                        <span className="truncate">{mentor.full_name}</span>
                        {mentor.is_verified && <CheckCircle className="h-4 w-4 text-[#FFE500] flex-shrink-0" />}
                      </h3>
                      <p className="text-sm text-[#A0A0A0]">@{mentor.username}</p>
                    </div>
                  </div>

                  {mentor.bio && <p className="text-sm text-[#A0A0A0] line-clamp-2 mb-4">{mentor.bio}</p>}

                  {mentor.available_slots?.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {[...new Set(mentor.available_slots.map((s) => s.session_type))].slice(0, 3).map((type) => {
                          const session = sessionTypes[type] || { label: type, icon: Calendar };
                          const Icon = session.icon;
                          return (
                            <span key={type} className="inline-flex items-center gap-1 bg-[#0A0B0F] text-[#A0A0A0] px-3 py-1 rounded-full text-xs">
                              <Icon className="h-3 w-3" />
                              {session.label}
                            </span>
                          );
                        })}
                      </div>
                      <p className="text-sm text-[#A0A0A0] mb-4">
                        Starting{' '}
                        <span className="text-[#FFE500] font-bold">
                          ₹{Math.min(...mentor.available_slots.map((s) => s.price))}
                        </span>
                        /session
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-[#666] mb-4">No slots available</p>
                  )}

                  <div className="flex gap-2">
                    <Link to={`/${mentor.username}`} className="flex-1">
                      <button className="w-full pill-btn pill-btn-inactive">Profile</button>
                    </Link>
                    <Link to={`/book/${mentor.username}`} className="flex-1">
                      <button className="w-full pill-btn pill-btn-active" data-testid={`book-${mentor.username}`}>
                        Book
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-[#16181D] rounded-3xl">
              <GraduationCap className="h-16 w-16 mx-auto mb-4 text-[#666]" />
              <h3 className="text-xl font-bold text-white mb-2">No Mentors Found</h3>
              <p className="text-[#A0A0A0] mb-6">Be the first to offer mentorship on M4manna!</p>
              <Link to="/register">
                <button className="pill-btn pill-btn-active">Become a Mentor</button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Mentors;
